// =====================================================================
// Salary & Tax Declaration — self-service page for every role except
// admin. Opened from the sidebar "Payroll & Tax Summary" item. Three
// tabs:
//   1. Salary Declaration   — CTC + effective date + component split
//                             (Basic ≥ 40% CTC, HRA ≥ 50% Basic floors).
//   2. Investment Declaration — tax regime + deduction fields (80C, etc).
//   3. Earnings             — read-only computed breakdown (gross, EPF,
//                             ESIC, PT, LWF, TDS, net, CTC).
//
// Persistence reuses the existing employee get/update API — the current
// user's employee record is resolved by user_id (same pattern as
// Documents.tsx) and saved back via updateEmployee.
// =====================================================================

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Wallet,
  PiggyBank,
  Calculator,
  Info,
  Save,
  Loader2,
  Home,
  Plane,
  X,
  Landmark,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/useToast";
import BankDetailsModal from "../../components/BankDetailsModal";
import {
  getEmployees,
  getEmployeeById,
  getMyEmployeeProfile,
  updateEmployee,
} from "../../api/employees";
import { getCompanyComplianceSettings } from "../../api/officeandcompany";
import {
  calcPT,
  lwfMonthly,
  fmt,
  num,
  salaryFloors,
  calcHraExemption,
  calcLtaExemption,
  calcFyTax,
  currentFinancialYear,
  financialYearOptions,
  fyBounds,
  monthsActiveInFy,
  MEAL_VOUCHER_ANNUAL_CAP,
  type FySlice,
} from "../../utils/employee.constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

type SalaryRecord = {
  id: string;
  financialYear: string;
  effectiveDate: string;
  endDate: string | null;
  ctc: number; // annual CTC for this component
  pfEmployeeRate: number;
  pfRateLocked: boolean;
  // Per-component EPF toggle. EPF is mandatory (forced on) when basicSalary
  // ≤ ₹15,000; above the ceiling this flag decides whether EPF is deducted for
  // the months this component is in force.
  pfApplicable: boolean;
  // Per-component declaration lock. "Save Declaration" finalises ONLY the
  // component being edited (the latest one), so a later admin-added increment
  // component loads unlocked until the employee saves it in turn.
  declarationLocked: boolean;
  basicSalary: number;
  hra: number;
  conveyance: number;
  medicalallowance: number;
  mealAllowance: number;
  // How the meal allowance is paid: food "voucher" (coupons) or "cash".
  mealAllowanceType: "voucher" | "cash";
  allowance: number;
  lta: number;
  others: number;
  bonusAmount: number;
  customComponents: { id: string; name: string; amount: number }[];
};

type Declaration = {
  sec80c: number;
  sec80tta: number;
  sec80d: number;
  sec80g: number;
  sec80eea: number;
  sec80e: number; // Interest on education loan — 80E
  sec80u: number; // Disability — 80U/80DD
  sec80ccd1b: number; // Employee NPS — 80CCD
  npsEmployer: number; // Employer NPS — 80CCD(2)
  otherDed: number;
  hraExempt: number;
  homeLoan: number;
  ltaExempt: number;
  ptPaid: number;
};

const EMPTY_RECORD = (): SalaryRecord => ({
  id: "current",
  financialYear: currentFinancialYear(),
  effectiveDate: new Date().toISOString().split("T")[0],
  endDate: null,
  ctc: 0,
  pfEmployeeRate: 12,
  pfRateLocked: false,
  pfApplicable: true,
  declarationLocked: false,
  basicSalary: 0,
  hra: 0,
  conveyance: 0,
  medicalallowance: 0,
  mealAllowance: 0,
  mealAllowanceType: "cash",
  allowance: 0,
  lta: 0,
  others: 0,
  bonusAmount: 0,
  customComponents: [],
});

// Carry-forward: when a financial year has no salary component of its own, the
// employee's last still-running salary (effective BEFORE the FY start, with no
// end date or an end date inside/after the FY) continues into it. Returns that
// component re-stamped to start on 1-Apr of the FY (so the whole FY uses it), or
// null when no active salary carries in. The PF-rate lock is reset — a new FY is
// a fresh declaration. A plain "YYYY-04-01" string is used (not toISOString) to
// avoid the +0530 shift that would mis-bucket 1-Apr into the previous FY.
function findCarryForwardRecord(records: SalaryRecord[], fyLabel: string): SalaryRecord | null {
  const startYear = fyLabel.split("-")[0];
  const fyStart = new Date(`${startYear}-04-01`);
  const inForce = records
    .filter((r) => {
      if (!r.effectiveDate) return false;
      if (new Date(r.effectiveDate) >= fyStart) return false; // its own FY handles it
      if (r.endDate && new Date(r.endDate) < fyStart) return false; // ended before the FY
      return true;
    })
    .sort((a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime());
  const src = inForce[inForce.length - 1];
  if (!src) return null;
  return {
    ...src,
    id: `carry-${fyLabel}`,
    financialYear: fyLabel,
    effectiveDate: `${startYear}-04-01`,
    endDate: null,
    pfRateLocked: false,
    // A carried-forward year is a fresh declaration — open until the employee
    // saves it for the new FY.
    declarationLocked: false,
  };
}

const EMPTY_DECLARATION = (): Declaration => ({
  sec80c: 0,
  sec80tta: 0,
  sec80d: 0,
  sec80g: 0,
  sec80eea: 0,
  sec80e: 0,
  sec80u: 0,
  sec80ccd1b: 0,
  npsEmployer: 0,
  otherDed: 0,
  hraExempt: 0,
  homeLoan: 0,
  ltaExempt: 0,
  ptPaid: 0,
});

// Local draft of the in-progress declaration. The exemption claims (HRA/LTA)
// and investment fields only reach the server when "Save Declaration" is
// clicked — which also locks the declaration. Mirroring the working draft to
// localStorage lets those edits survive a refresh or page change before that
// final save, instead of silently resetting to the last saved (or empty) value.
const DECL_DRAFT_KEY = (employeeId: string) => `salaryTaxDeclDraft:${employeeId}`;
// The raw "actual travel expense" the employee typed for LTA. Only the capped
// exemption (min of this and LTA received) is persisted server-side, so this
// input value is remembered separately to re-populate the modal as entered.
const LTA_TRAVEL_KEY = (employeeId: string) => `ltaTravelExpense:${employeeId}`;
// The HRA calculator's user-entered inputs (rent paid, metro/non-metro, pays
// rent). Only the computed exemption is stored server-side, so these are kept
// separately so the modal re-populates as entered after a refresh. HRA received
// is intentionally excluded — it's re-derived from the payslip HRA on open.
const HRA_CALC_KEY = (employeeId: string) => `hraCalcInputs:${employeeId}`;
// Unsaved salary-structure + EPF-choice draft (component amounts, the EPF
// opt-in/out + PF basis/rate). These live only in React state until "Save
// Declaration", so without a draft they reset on every refresh/page change —
// e.g. an EPF opt-in (and the PF value it surfaces on the Investment tab) would
// vanish. Persisted per employee + per component id; cleared once saved/locked.
const SALARY_DRAFT_KEY = (employeeId: string) => `salaryStructDraft:${employeeId}`;
// Editable fields restored from the salary draft. Server-controlled fields
// (id, effectiveDate, endDate, ctc, financialYear, the lock flags) are NEVER
// taken from the draft so an admin change to CTC/effective-date still wins.
const SALARY_DRAFT_FIELDS = [
  "basicSalary", "hra", "conveyance", "medicalallowance", "mealAllowance",
  "mealAllowanceType", "allowance", "lta", "others", "bonusAmount",
  "customComponents", "pfApplicable", "pfEmployeeRate",
] as const;

// Canonical component order: oldest → newest, so the LAST entry is always the
// live/editable component and everything before it is a closed, locked slice.
// The API returns salary components UNORDERED and two components can share an
// effective date, so the row id breaks the tie — the admin's Employee form
// sorts by the same key (descending) and the two screens therefore always agree
// on which component is the current one.
function byEffectiveDateAsc(a: SalaryRecord, b: SalaryRecord): number {
  const byDate = new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime();
  return byDate !== 0 ? byDate : String(a.id).localeCompare(String(b.id));
}

function mergeDeclarationDraft(
  server: Declaration,
  employeeId: string,
  locked: boolean,
): Declaration {
  if (locked || !employeeId) return server;
  try {
    const raw = localStorage.getItem(DECL_DRAFT_KEY(employeeId));
    if (!raw) return server;
    const draft = JSON.parse(raw);
    if (draft && typeof draft === "object") return { ...server, ...draft };
  } catch {
    /* corrupt/unavailable storage → fall back to server values */
  }
  return server;
}

export default function SalaryTaxDeclaration() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [employeeName, setEmployeeName] = useState("");
  // Interns bypass ALL statutory compliance (PF / ESIC / PT / LWF) and income
  // tax (TDS): their stipend is paid in full, net = gross. This flag gates
  // every deduction/tax computation below.
  const [isIntern, setIsIntern] = useState(false);
  // Bank-details popup (opened from the header button). The modal reads/writes
  // the current user's own employee record.
  const [bankModalOpen, setBankModalOpen] = useState(false);

  const [ctcValue, setCtcValue] = useState(0); // annual or monthly per isYearly
  const [isYearly, setIsYearly] = useState(true);
  // All salary components for this employee (across financial years), plus the
  // FY currently being viewed. `record` is the latest (editable) component of
  // the selected FY; older components are shown read-only.
  const [allRecords, setAllRecords] = useState<SalaryRecord[]>([]);
  const [financialYear, setFinancialYear] = useState<string>(currentFinancialYear());
  // PF-contribution chooser (opened from the button in the Salary Declaration
  // header). The employee picks the PF base: "actual" (full Basic, at their own
  // %) or "statutory"/capped (min(Basic, ₹15,000) at the standard 12%). It can
  // be changed freely until the declaration is saved, after which it's locked
  // along with the rest of the declaration. pfBasisDraft/pfRateDraft hold the
  // in-modal selection.
  const [pfModalOpen, setPfModalOpen] = useState(false);
  const [pfBasisDraft, setPfBasisDraft] = useState<"statutory" | "actual">("statutory");
  const [pfRateDraft, setPfRateDraft] = useState(12);
  // True once "Save Declaration" has been submitted — the whole declaration
  // becomes read-only and nothing more can be changed.
  const [declarationLocked, setDeclarationLocked] = useState(false);
  // PF rate the admin/company set (from the per-employee TaxConfig or company
  // compliance). Used as the default until the employee picks their own.
  const [companyPfRate, setCompanyPfRate] = useState(12);
  // Older (read-only) components are collapsed by default; this set holds the
  // ids the employee has expanded.
  const [expandedEmpIds, setExpandedEmpIds] = useState<Set<string>>(new Set());
  const toggleEmpExpanded = (id: string) =>
    setExpandedEmpIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const [record, setRecord] = useState<SalaryRecord>(EMPTY_RECORD());
  const [declaration, setDeclaration] = useState<Declaration>(EMPTY_DECLARATION());
  const [taxRegime, setTaxRegime] = useState<"new" | "old">("new");
  // Old Regime isn't ready yet — clicking it shows an "in progress" notice
  // instead of switching.
  const [oldRegimeNotice, setOldRegimeNotice] = useState(false);
  const [tdsApplicable, setTdsApplicable] = useState(true);

  // Statutory config (defaults from company compliance, overridden by the
  // employee's own TaxConfig row when present).
  const [pfApplicable, setPfApplicable] = useState(true);
  // Employees whose monthly Basic is above the ₹15,000 EPF wage ceiling may opt
  // out of EPF. When opted out, no PF is deducted and the admin sees the status.
  // UAN / PF member ID are optional identifiers captured when opted in.
  const [pfOptOut, setPfOptOut] = useState(false);
  const [uan, setUan] = useState("");
  const [pfMemberId, setPfMemberId] = useState("");
  const [pfBasis, setPfBasis] = useState<"statutory" | "actual">("statutory");
  // Employer PF is fixed at 12%. The employee may raise their own rate
  // (never below 12%). pfFromCtc: when true the employer's 12% is part of
  // CTC and reduces take-home.
  const [pfEmployeeRate, setPfEmployeeRate] = useState(12);
  const [pfFromCtc, setPfFromCtc] = useState(false);
  const [esicApplicable, setEsicApplicable] = useState(false);
  const [ptApplicable, setPtApplicable] = useState(true);
  const [ptState, setPtState] = useState("karnataka");
  const [lwfApplicable, setLwfApplicable] = useState(false);
  const [lwfState, setLwfState] = useState("karnataka");

  // Tax-computation breakdown is collapsed behind a button by default.
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);
  // Month/slab-wise tax table is collapsed by default.
  const [showSlabTable, setShowSlabTable] = useState(false);

  // Controlled tabs so the "Next" buttons can advance the active tab.
  const [activeTab, setActiveTab] = useState<"salary" | "investment" | "earnings">("salary");
  const goToTab = (tab: "salary" | "investment" | "earnings") => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // HRA rent-exemption calculator (old regime). Drives declaration.hraExempt.
  const [hraCalcOpen, setHraCalcOpen] = useState(false);
  const [paysRent, setPaysRent] = useState(true);
  const [annualRent, setAnnualRent] = useState(0);
  const [annualHraReceived, setAnnualHraReceived] = useState(0);
  const [isMetro, setIsMetro] = useState(true);

  // LTA travel-exemption claim (old regime). Drives declaration.ltaExempt.
  const [ltaCalcOpen, setLtaCalcOpen] = useState(false);
  const [travelExpense, setTravelExpense] = useState(0);

  const monthlyCtc = isYearly ? ctcValue / 12 : ctcValue;

  // Per-component EPF. EPF is statutorily MANDATORY at/below the ₹15,000 basic
  // ceiling; above it the component's own pfApplicable flag decides. The
  // establishment-level `pfApplicable` still gates everything (a company that
  // doesn't run EPF never deducts). `recordPfOn(r)` is the effective per-period
  // flag used by the salary engine for ANY component.
  const recordPfOn = (r: SalaryRecord) =>
    pfApplicable && !isIntern && (num(r.basicSalary) <= 15000 ? true : r.pfApplicable !== false);
  // EPF can be toggled for the current component only above the ₹15,000 ceiling.
  const canToggleCurrentPf = num(record.basicSalary) > 15000;
  // True when the current component has EPF switched off (only possible above
  // the ceiling). Drives the header toggle / chooser state.
  const currentPfOff = canToggleCurrentPf && record.pfApplicable === false;
  const pfActive = recordPfOn(record);

  // ── Load: resolve current employee, then fetch full detail + company
  // compliance defaults and map into local state. ──────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const truthy = (v: any) => v === true || v === 1 || v === "true";

        // 1. Find this user's employee record (same approach as Documents).
        const res = await getEmployees();
        const all = res.employees || [];
        const email = ((user as any)?.email || "").toString().toLowerCase();
        let me =
          all.find((e: any) => e.user_id === user?.id) ||
          (email
            ? all.find((e: any) => (e.email || "").toLowerCase() === email)
            : undefined);

        // `/employeesAll` is role-scoped — managers/HR heads see only their
        // reports and admins are filtered out — so the caller is often absent
        // from that list. Fall back to the self-lookup endpoint, which resolves
        // the caller's own row from the token regardless of role.
        if (!me?.id) {
          try {
            const mine = await getMyEmployeeProfile();
            if (mine?.id) me = mine;
          } catch {
            /* no employee profile for this user → show the error below */
          }
        }

        if (!me?.id) {
          setLoadError("Could not find your employee profile.");
          return;
        }
        setEmployeeId(me.id);

        // 2. Full detail (nested shapes tolerated, mirrors Documents.tsx).
        const detailRes: any = await getEmployeeById(me.id);
        const emp =
          detailRes?.employee?.employee ||
          detailRes?.employee ||
          detailRes ||
          {};
        setEmployeeName(
          `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || me.name || "",
        );

        // 3. Company compliance defaults.
        let settings: any = null;
        try {
          const compliance = await getCompanyComplianceSettings();
          if (Array.isArray(compliance)) settings = compliance[0];
          else if (compliance?.data)
            settings = Array.isArray(compliance.data) ? compliance.data[0] : compliance.data;
          else settings = compliance;
        } catch {
          /* no company record yet → keep defaults */
        }
        settings = settings || {};

        // Per-employee TaxConfig row overrides company defaults when present.
        
        const tax = emp?.TaxConfig?.[0];
        const pickTax = (field: string, fallback: any) => {
          const v = tax ? (tax as any)[field] : undefined;
          return v === undefined || v === null || v === "" ? fallback : v;
        };
        // Interns are exempt from every compliance component and income tax —
        // force all statutory toggles OFF regardless of the company/tax config
        // so nothing is ever deducted from their stipend.
        const intern = (emp.role || "").toString().trim().toLowerCase() === "intern";
        setIsIntern(intern);
        setPfApplicable(!intern && truthy(tax ? tax.pfApplicable : settings.pfApplicable));
        setEsicApplicable(!intern && truthy(tax ? tax.esicApplicable : settings.esicApplicable));
        setPtApplicable(!intern && truthy(tax ? tax.ptApplicable : settings.ptApplicable));
        setLwfApplicable(!intern && truthy(tax ? tax.lwfApplicable : settings.lwfApplicable));
        setTdsApplicable(!intern && truthy(tax ? tax.tdsApplicable : settings.tdsApplicable));
        setPfBasis(pickTax("pfBasis", settings.pfBasis || "statutory") as any);
        setPtState(pickTax("ptState", settings.ptState || "karnataka"));
        setLwfState(pickTax("lwfState", settings.lwfState || "karnataka"));
        setTaxRegime(pickTax("taxRegime", settings.taxRegime || "new") as any);
        setPfFromCtc(truthy(settings.pfFromCtc));
        // Employee PF rate: the employee's own saved choice, else the company
        // default, never below 12%.
        const adminPfRate = Math.max(
          12,
          num(pickTax("pfEmployeeRate", emp.pfEmployeeRate || settings.pfEmployeeRate || 12)),
        );
        setCompanyPfRate(adminPfRate);
        setPfEmployeeRate(adminPfRate);
        // The declaration lock is now PER COMPONENT — it's set from the resolved
        // current component (`rec.declarationLocked`) below, once the salary
        // components have been mapped, not from the per-employee TaxConfig.
        // EPF opt-out + optional identifiers.
        setPfOptOut(truthy(pickTax("pfOptOut", emp.pfOptOut)));
        setUan(String(pickTax("uan", emp.uan ?? "") ?? ""));
        setPfMemberId(String(pickTax("pfMemberId", emp.pfMemberId ?? "") ?? ""));

        // 4. Salary components — load ALL of them (each financial year /
        // mid-year revision). The admin-added components must be visible here.
        const sdList: any[] = Array.isArray(emp?.SalaryDetails) ? emp.SalaryDetails : [];
        const mappedAll: SalaryRecord[] = sdList.map((sd: any, i: number) => {
          const effectiveDate =
            (sd.effective_date || sd.effectiveDate || sd.date || "")?.toString().split("T")[0] ||
            emp.hire_date?.split("T")[0] ||
            EMPTY_RECORD().effectiveDate;
          return {
            id: sd.id || `rec-${i}`,
            // FY follows the effective date (the source of truth), so a record
            // previously saved under the wrong financial_year still lands in
            // the correct year here and in every fyOfRec() grouping below.
            financialYear: currentFinancialYear(new Date(effectiveDate)),
            effectiveDate,
            endDate: (sd.end_date || "")?.toString().split("T")[0] || null,
            ctc: num(sd.ctc),
            pfEmployeeRate: num(sd.pf_employee_rate) || num(sd.pfEmployeeRate) || 12,
            pfRateLocked: !!(sd.pf_rate_locked ?? sd.pfRateLocked),
            // Per-component EPF flag. Defaults ON when the column is absent
            // (legacy rows) so existing employees keep deducting EPF.
            pfApplicable: (sd.pf_applicable ?? sd.pfApplicable) !== false,
            // Per-component declaration lock. Defaults OFF (open) when absent so
            // a newly-added component is editable until the employee saves it.
            declarationLocked: !!(sd.declaration_locked ?? sd.declarationLocked),
            basicSalary: num(sd.basicsalary),
            hra: num(sd.hra),
            conveyance: num(sd.conveyance),
            medicalallowance: num(sd.medicalallowance),
            mealAllowance: num(sd.mealallowance),
            mealAllowanceType: (sd.meal_allowance_type ?? sd.mealAllowanceType) === "voucher" ? "voucher" : "cash",
            allowance: num(sd.allowance),
            lta: num(sd.lta),
            others: num(sd.others),
            bonusAmount: num(sd.bonus),
            customComponents: sd.customComponents || [],
          };
        });
        // A component with a strictly later component after it is superseded, so
        // it is closed and locked whether or not the stored flag says so — the
        // admin's save may not have written the flag yet. Deriving it here is
        // what keeps the previous component from looking editable until the NEXT
        // component is added.
        const newestEffective = mappedAll.reduce(
          (max, r) => (r.effectiveDate ? Math.max(max, new Date(r.effectiveDate).getTime()) : max),
          -Infinity,
        );
        mappedAll.forEach((r) => {
          const t = r.effectiveDate ? new Date(r.effectiveDate).getTime() : -Infinity;
          if (t < newestEffective) r.declarationLocked = true;
        });
        setAllRecords(mappedAll);

        // Always default the filter to the CURRENT financial year. The editable
        // `record` is that FY's latest-effective component, or a blank record
        // tagged with the current FY when the employee has none there yet.
        const fy = currentFinancialYear();
        setFinancialYear(fy);
        const inFy = [...mappedAll]
          .filter((r) => (r.effectiveDate ? currentFinancialYear(new Date(r.effectiveDate)) : r.financialYear) === fy)
          .sort(byEffectiveDateAsc);
        const latest = inFy[inFy.length - 1];
        // No own component this FY? Carry forward the last still-running salary.
        const carry = latest ? null : findCarryForwardRecord(mappedAll, fy);
        let rec: SalaryRecord =
          latest ||
          carry ||
          { ...EMPTY_RECORD(), id: `empty-${fy}`, financialYear: fy, effectiveDate: `${fy.split("-")[0]}-04-01` };
        // PF rate is per component: the current (latest, editable) component
        // keeps its OWN rate — it no longer inherits an older locked component's
        // rate, so each component can have a different PF %.
        // Per-component lock: the page is read-only only when the component the
        // employee is editing (the latest one) is itself locked. A fresh
        // admin-added increment loads unlocked.
        setDeclarationLocked(!!rec.declarationLocked);
        let pfRateForState = Math.max(12, num(rec.pfEmployeeRate) || adminPfRate || 12);

        // Restore any unsaved salary/EPF draft for THIS component so in-progress
        // edits (incl. the EPF opt-in and PF basis/rate) survive a refresh or
        // page change — applied only while the component is unlocked, exactly
        // like the investment-declaration draft. Keyed to rec.id so a draft for
        // a superseded component never bleeds into a new one.
        if (!rec.declarationLocked) {
          try {
            const sRaw = localStorage.getItem(SALARY_DRAFT_KEY(me.id));
            if (sRaw) {
              const sDraft = JSON.parse(sRaw);
              if (sDraft && typeof sDraft === "object" && sDraft.recordId === rec.id && sDraft.record) {
                const editable: Partial<SalaryRecord> = {};
                for (const k of SALARY_DRAFT_FIELDS) {
                  if (sDraft.record[k] !== undefined) (editable as any)[k] = sDraft.record[k];
                }
                rec = { ...rec, ...editable };
                if (typeof sDraft.pfApplicable === "boolean") setPfApplicable(sDraft.pfApplicable);
                if (sDraft.pfBasis === "statutory" || sDraft.pfBasis === "actual") setPfBasis(sDraft.pfBasis);
                if (num(sDraft.pfEmployeeRate) >= 12) pfRateForState = Math.max(12, num(sDraft.pfEmployeeRate));
              }
            }
          } catch {
            /* corrupt/unavailable storage → fall back to server values */
          }
        }
        setRecord(rec);
        setPfEmployeeRate(pfRateForState);
        const sd = latest || carry; // used for CTC seeding below

        // 5. Declaration fields — persisted on the per-employee TaxConfig row,
        // so read via pickTax. sec80tta/sec80eea have no backing column yet.
        const serverDeclaration: Declaration = {
          sec80c: num(pickTax("sec80c", emp.sec80c)),
          sec80tta: num(emp.sec80tta),
          sec80d: num(pickTax("sec80d", emp.sec80d)),
          sec80g: num(pickTax("sec80g", emp.sec80g)),
          sec80eea: num(emp.sec80eea),
          sec80e: num(pickTax("sec80e", emp.sec80e)),
          sec80u: num(pickTax("sec80u", emp.sec80u)),
          sec80ccd1b: num(pickTax("sec80ccd1b", emp.sec80ccd1b)),
          npsEmployer: num(pickTax("npsEmployer", emp.npsEmployer)),
          otherDed: num(pickTax("otherDed", emp.otherDed)),
          hraExempt: num(pickTax("hraExempt", emp.hraExempt)),
          homeLoan: num(pickTax("homeLoan", emp.homeLoan)),
          ltaExempt: num(pickTax("ltaExempt", emp.ltaExempt)),
          ptPaid: num(pickTax("ptPaid", emp.ptPaid)),
        };
        // Restore any unsaved local draft so applied HRA/LTA exemptions and
        // other declaration edits survive a refresh or page change before the
        // declaration is finalised. The draft is cleared once it's saved/locked.
        const isLocked = !!rec.declarationLocked;
        const finalDeclaration = mergeDeclarationDraft(serverDeclaration, me.id, isLocked);
        setDeclaration(finalDeclaration);
        // Restore the last-entered LTA travel expense so reopening the modal
        // shows what was typed (e.g. 60,000), not the capped exemption. Falls
        // back to the saved exemption when nothing was stored.
        try {
          const tRaw = localStorage.getItem(LTA_TRAVEL_KEY(me.id));
          setTravelExpense(
            tRaw !== null && tRaw !== "" ? num(tRaw) : num(finalDeclaration.ltaExempt),
          );
        } catch {
          setTravelExpense(num(finalDeclaration.ltaExempt));
        }
        // Restore the HRA calculator's user inputs (rent / metro / pays-rent) so
        // the modal re-shows them after a refresh — only the exemption itself is
        // stored server-side.
        try {
          const hRaw = localStorage.getItem(HRA_CALC_KEY(me.id));
          if (hRaw) {
            const h = JSON.parse(hRaw);
            if (h && typeof h === "object") {
              if (typeof h.paysRent === "boolean") setPaysRent(h.paysRent);
              if (h.annualRent != null) setAnnualRent(num(h.annualRent));
              if (typeof h.isMetro === "boolean") setIsMetro(h.isMetro);
            }
          }
        } catch {
          /* corrupt/unavailable storage → keep defaults */
        }

        const gross =
          num(sd?.basicSalary) + num(sd?.hra) + num(sd?.conveyance) +
          num(sd?.medicalallowance) + num(sd?.mealAllowance) + num(sd?.allowance) +
          num(sd?.lta) + num(sd?.others);
        const storedCtc = num(sd?.ctc);
        if (storedCtc > 0) {
          setCtcValue(Math.round(storedCtc));
          setIsYearly(true);
        } else if (gross > 0) {
          setCtcValue(Math.round(gross * 12));
          setIsYearly(true);
        }
      } catch (err: any) {
        setLoadError(err?.message || "Failed to load your salary details.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Mirror the in-progress declaration to a local draft so applied HRA/LTA
  // exemptions and investment edits survive a refresh or page change before the
  // declaration is saved. Skipped while loading and once the declaration is
  // locked (a saved declaration is the source of truth).
  useEffect(() => {
    if (loading || !employeeId || declarationLocked) return;
    try {
      localStorage.setItem(DECL_DRAFT_KEY(employeeId), JSON.stringify(declaration));
    } catch {
      /* storage full/unavailable → draft persistence is best-effort */
    }
  }, [declaration, employeeId, declarationLocked, loading]);

  // Remember the raw LTA travel expense the employee typed so the modal can
  // re-show it as entered (the server only stores the capped exemption).
  useEffect(() => {
    if (loading || !employeeId || declarationLocked) return;
    try {
      localStorage.setItem(LTA_TRAVEL_KEY(employeeId), String(travelExpense));
    } catch {
      /* best-effort */
    }
  }, [travelExpense, employeeId, declarationLocked, loading]);

  // Remember the HRA calculator's user inputs (rent / metro / pays-rent) so the
  // modal re-shows them after a refresh (the server only stores the exemption).
  useEffect(() => {
    if (loading || !employeeId || declarationLocked) return;
    try {
      localStorage.setItem(
        HRA_CALC_KEY(employeeId),
        JSON.stringify({ paysRent, annualRent, isMetro }),
      );
    } catch {
      /* best-effort */
    }
  }, [paysRent, annualRent, isMetro, employeeId, declarationLocked, loading]);

  // Mirror the in-progress salary structure + EPF choice (component amounts,
  // EPF opt-in/out, PF basis/rate) to a local draft so they survive a refresh or
  // page change before "Save Declaration" — without locking. Skipped while
  // loading and once the current component is locked.
  useEffect(() => {
    if (loading || !employeeId || declarationLocked) return;
    try {
      const editable: Record<string, any> = {};
      for (const k of SALARY_DRAFT_FIELDS) editable[k] = (record as any)[k];
      localStorage.setItem(
        SALARY_DRAFT_KEY(employeeId),
        JSON.stringify({ recordId: record.id, record: editable, pfApplicable, pfBasis, pfEmployeeRate }),
      );
    } catch {
      /* best-effort */
    }
  }, [record, pfApplicable, pfBasis, pfEmployeeRate, employeeId, declarationLocked, loading]);

  // A component's financial year. The Indian FY (1-Apr → 31-Mar) is fixed by
  // the EFFECTIVE DATE, so derive from it first; only fall back to a stored
  // financialYear when there's no effective date. This also corrects display
  // of any component that was previously saved under the wrong FY filter.
  const fyOfRec = (r: SalaryRecord) =>
    r.effectiveDate
      ? currentFinancialYear(new Date(r.effectiveDate))
      : (r.financialYear || currentFinancialYear());

  // Older (read-only) components of the FY being viewed — everything except the
  // live, editable current record. The latest component stays editable.
  const olderRecords = useMemo(
    () =>
      allRecords
        .filter((r) => r.id !== record.id && fyOfRec(r) === financialYear)
        .sort(byEffectiveDateAsc),
    [allRecords, record.id, financialYear],
  );
  const recordInFy = fyOfRec(record) === financialYear;
  // Whether the selected FY actually has any salary component on record.
  const fyHasComponents = allRecords.some((r) => fyOfRec(r) === financialYear);
  // The shown salary is carried forward from a previous FY's still-running
  // component — applied to this FY but not yet saved as its own component.
  const isCarriedForward = recordInFy && record.id.startsWith("carry-");
  // All components of the FY, oldest → newest (current/editable last).
  const visibleRecords = useMemo(
    () => (recordInFy ? [...olderRecords, record] : olderRecords),
    [olderRecords, record, recordInFy],
  );

  // Full monthly breakdown for any component, using the current statutory
  // config and that component's own PF rate.
  const monthlyOf = (r: SalaryRecord) => {
    const basic = num(r.basicSalary);
    const gross =
      basic + num(r.hra) + num(r.conveyance) + num(r.medicalallowance) +
      num(r.mealAllowance) + num(r.allowance) + num(r.lta) + num(r.others) +
      (r.customComponents?.reduce((s, c) => s + (c.amount || 0), 0) || 0);
    const pfBase = pfBasis === "statutory" ? Math.min(basic, 15000) : basic;
    const rate = Math.max(12, num(r.pfEmployeeRate) || 12);
    // PF is decided per component: mandatory at/below ₹15,000 basic, otherwise
    // this component's own EPF flag. A component with EPF off cuts no PF for the
    // months it is in force.
    const pfOn = recordPfOn(r);
    const pfEE = pfOn ? Math.round(pfBase * (rate / 100)) : 0;
    const pfER = pfOn ? Math.round(pfBase * 0.12) : 0;
    const eps = pfOn ? Math.round(Math.min(pfBase, 15000) * 0.0833) : 0;
    const edli = pfOn ? Math.round(Math.min(pfBase, 15000) * 0.005) : 0;
    const esicEE = esicApplicable && !isIntern && gross <= 21000 ? Math.round(gross * 0.0075) : 0;
    const esicER = esicApplicable && !isIntern && gross <= 21000 ? Math.round(gross * 0.0325) : 0;
    const pt = ptApplicable && !isIntern ? calcPT(gross, ptState) : 0;
    const lwfData = lwfMonthly(lwfState);
    const lwfEE = lwfApplicable && !isIntern ? Math.round(lwfData.ee) : 0;
    const lwfER = lwfApplicable && !isIntern ? Math.round(lwfData.er) : 0;
    // Meal allowance paid as a VOUCHER is tax-exempt (old regime, up to the
    // annual cap); paid as CASH it's fully taxable.
    const mealVoucher = (r.mealAllowanceType || "cash") === "voucher" ? num(r.mealAllowance) : 0;
    return { gross, pfEE, pfER, eps, edli, esicEE, esicER, pt, lwfEE, lwfER, mealVoucher };
  };

  // FY-prorated tax: blend every component of the FY for the months its CTC
  // was in force (same engine the admin uses), then spread over months worked.
  const fyTax = useMemo(() => {
    const { start, end } = fyBounds(financialYear);
    const slices: FySlice[] = visibleRecords.map((r, i) => {
      const m = monthlyOf(r);
      const effStart = r.effectiveDate ? new Date(r.effectiveDate) : null;
      const next = visibleRecords[i + 1];
      let effEnd: Date | null = null;
      if (next?.effectiveDate) {
        effEnd = new Date(next.effectiveDate);
        effEnd.setDate(effEnd.getDate() - 1);
      }
      return {
        monthlyGross: m.gross,
        months: monthsActiveInFy(effStart, effEnd, start, end),
        pfEEMonthly: m.pfEE,
        pfERMonthly: m.pfER,
        mealVoucherMonthly: m.mealVoucher,
      };
    });
    const base = calcFyTax(slices, taxRegime, declaration, { taxableIncome: 0, tdsPaid: 0 });
    // Interns pay no income tax; zero out the tax figures regardless of config.
    return (tdsApplicable && !isIntern) ? base : { ...base, annualTax: 0, balanceTax: 0, monthlyTds: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRecords, financialYear, taxRegime, declaration, tdsApplicable, isIntern, pfApplicable, pfActive, pfBasis]);

  // Month-by-month breakdown of the financial year. EPF and TDS are NOT
  // constant: each month uses whichever component is in force, and the whole
  // year's income tax is deducted only in the months where the salary is above
  // ₹12,00,000 (annualised) — below that the new-regime rebate makes tax zero.
  const MONTH_NAMES = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
  const monthWiseTax = useMemo(() => {
    const { start } = fyBounds(financialYear);
    type MRow = {
      label: string; active: boolean; gross: number; pfEE: number; pfER: number;
      esicEE: number; esicER: number; pt: number; lwfEE: number; mealVoucher: number; isHigh: boolean; tax: number; net: number;
    };
    const data: MRow[] = [];
    for (let m = 0; m < 12; m++) {
      const monthStart = new Date(start.getFullYear(), start.getMonth() + m, 1);
      // The component that owns this month = the latest one in force on its LAST
      // day, so a mid-month effective date (e.g. 5 Jan) claims that whole month
      // instead of the salary only starting the following month.
      const monthEnd = new Date(start.getFullYear(), start.getMonth() + m + 1, 0);
      const active = [...visibleRecords]
        .filter((r) => r.effectiveDate && new Date(r.effectiveDate) <= monthEnd)
        .sort((a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime())[0];
      const mo = active ? monthlyOf(active) : { gross: 0, pfEE: 0, pfER: 0, esicEE: 0, esicER: 0, pt: 0, lwfEE: 0, mealVoucher: 0 } as any;
      const annualCtc = active ? (num(active.ctc) || mo.gross * 12) : 0;
      data.push({
        label: `${MONTH_NAMES[m]} ${monthStart.getFullYear()}`,
        active: !!active,
        gross: mo.gross, pfEE: mo.pfEE, pfER: mo.pfER, esicEE: mo.esicEE, esicER: mo.esicER, pt: mo.pt, lwfEE: mo.lwfEE, mealVoucher: mo.mealVoucher || 0,
        isHigh: !!active && annualCtc > 1200000,
        tax: 0, net: 0,
      });
    }
    // Spread the whole-year tax across the months where salary is above ₹12L;
    // if no month crosses ₹12L, fall back to every active month.
    const totalTax = (tdsApplicable && !isIntern) ? Math.round(fyTax.annualTax) : 0;
    const highIdx = data.map((d, i) => (d.isHigh ? i : -1)).filter((i) => i >= 0);
    const taxIdx = highIdx.length > 0 ? highIdx : data.map((d, i) => (d.active ? i : -1)).filter((i) => i >= 0);
    const per = taxIdx.length ? Math.floor(totalTax / taxIdx.length) : 0;
    let remainder = totalTax - per * taxIdx.length;
    taxIdx.forEach((i, k) => {
      // Put the rounding remainder on the last tax month.
      data[i].tax = per + (k === taxIdx.length - 1 ? remainder : 0);
    });
    data.forEach((d) => {
      const employerPfInNet = pfFromCtc ? d.pfER : 0;
      // Employee PF is deducted from take-home in both regimes.
      const employeePfInNet = d.pfEE;
      d.net = d.active ? d.gross - (employeePfInNet + d.esicEE + d.pt + d.lwfEE + d.tax + employerPfInNet) : 0;
    });

    // Collapse consecutive months with the SAME salary into one slab period
    // (e.g. "Jul 2026 – Mar 2027") and total the tax/net across the period.
    type Period = {
      fromLabel: string; toLabel: string; months: number; active: boolean;
      gross: number; pfEE: number; pfER: number; esicEE: number; esicER: number; pt: number; lwfEE: number;
      isHigh: boolean; taxTotal: number; netTotal: number;
    };
    const periods: Period[] = [];
    data.forEach((d) => {
      const last = periods[periods.length - 1];
      const sameSlab =
        last && last.gross === d.gross && last.pfEE === d.pfEE && last.isHigh === d.isHigh && last.active === d.active;
      if (sameSlab) {
        last.toLabel = d.label;
        last.months += 1;
        last.taxTotal += d.tax;
        last.netTotal += d.net;
      } else {
        periods.push({
          fromLabel: d.label, toLabel: d.label, months: 1, active: d.active,
          gross: d.gross, pfEE: d.pfEE, pfER: d.pfER, esicEE: d.esicEE, esicER: d.esicER, pt: d.pt, lwfEE: d.lwfEE,
          isHigh: d.isHigh, taxTotal: d.tax, netTotal: d.net,
        });
      }
    });

    return { rows: data, periods, totalTax, taxMonthCount: taxIdx.length, highMonthCount: highIdx.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRecords, financialYear, fyTax.annualTax, tdsApplicable, isIntern, pfFromCtc, pfApplicable, pfActive, pfBasis, ptApplicable, ptState, lwfApplicable, lwfState, esicApplicable, taxRegime]);

  // Columns for the month-wise slab table. The EPF column shows the PF that
  // actually reduces take-home — employee PF in the OLD regime only, plus the
  // employer PF when it's part of CTC — so it lines up with the Net column. (In
  // the new regime the employee's own PF isn't deducted, so it isn't counted
  // here.) ESIC / Professional Tax / Labour Welfare columns appear only when
  // those deductions apply. Gross/EPF/ESIC/PT/LWF are per-month figures; Income
  // Tax and Net are period totals.
  type SlabPeriod = (typeof monthWiseTax.periods)[number];
  const sumSlab = (f: (r: (typeof monthWiseTax.rows)[number]) => number) =>
    monthWiseTax.rows.reduce((s, r) => s + f(r), 0);
  // PF deducted from take-home this month: employee PF (both regimes) plus
  // employer PF (only when part of CTC) — keeps the EPF column in step with Net.
  const pfInNet = (r: { pfEE: number; pfER: number }) =>
    r.pfEE + (pfFromCtc ? r.pfER : 0);
  // ESIC only applies up to the ₹21,000 gross threshold — show the column only
  // when the employee actually has ESIC deductions in the year.
  const hasEsic = monthWiseTax.rows.some((r) => r.esicEE + r.esicER > 0);
  const slabNumCols: {
    header: string; tone: string; cell: (p: SlabPeriod) => number; total: number;
  }[] = [
    { header: "Gross / month", tone: "gross", cell: (p) => p.gross, total: sumSlab((r) => r.gross) },
    { header: "EPF / month", tone: "ded", cell: (p) => pfInNet(p), total: sumSlab((r) => pfInNet(r)) },
    ...(hasEsic
      ? [{ header: "ESIC / month", tone: "ded", cell: (p: SlabPeriod) => p.esicEE + p.esicER, total: sumSlab((r) => r.esicEE + r.esicER) }]
      : []),
    ...(ptApplicable
      ? [{ header: "Prof. Tax / mo", tone: "ded", cell: (p: SlabPeriod) => p.pt, total: sumSlab((r) => r.pt) }]
      : []),
    ...(lwfApplicable
      ? [{ header: "Labour Welfare / mo", tone: "ded", cell: (p: SlabPeriod) => p.lwfEE, total: sumSlab((r) => r.lwfEE) }]
      : []),
    { header: "Income Tax", tone: "tax", cell: (p) => p.taxTotal, total: monthWiseTax.totalTax },
    { header: "Net (period)", tone: "net", cell: (p) => Math.round(p.netTotal), total: Math.round(sumSlab((r) => r.net)) },
  ];
  const slabColTemplate = `minmax(0, 1.6fr) repeat(${slabNumCols.length}, minmax(0, 1fr))`;

  // When the employee switches financial year, make that FY's latest component
  // the editable one and refresh the CTC / PF rate shown.
  useEffect(() => {
    setPfModalOpen(false);
    const inFy = allRecords
      .filter((r) => fyOfRec(r) === financialYear)
      .sort((a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime());
    const latest = inFy[inFy.length - 1];
    if (latest) {
      // PF rate is per component — the latest component keeps its own rate
      // instead of inheriting an older locked component's rate.
      const rec = latest;
      setRecord(rec);
      const storedCtc = num(latest.ctc);
      setCtcValue(storedCtc > 0 ? Math.round(storedCtc) : Math.round(monthlyOf(latest).gross * 12));
      setIsYearly(true);
      // Show the locked/component PF rate if set, otherwise the admin default.
      setPfEmployeeRate(Math.max(12, num(rec.pfEmployeeRate) > 0 ? num(rec.pfEmployeeRate) : companyPfRate));
    } else {
      // No component starts in this FY. Carry forward the last still-running
      // salary so the year shows it; otherwise a blank record tagged with the FY.
      const carry = findCarryForwardRecord(allRecords, financialYear);
      if (carry) {
        setRecord(carry);
        const storedCtc = num(carry.ctc);
        setCtcValue(storedCtc > 0 ? Math.round(storedCtc) : Math.round(monthlyOf(carry).gross * 12));
        setIsYearly(true);
        setPfEmployeeRate(Math.max(12, num(carry.pfEmployeeRate) > 0 ? num(carry.pfEmployeeRate) : companyPfRate));
      } else {
        // No salary on record at all → blank record tagged with the selected FY.
        setRecord({
          ...EMPTY_RECORD(),
          id: `empty-${financialYear}`,
          financialYear,
          // FY start (1 Apr) as a plain YYYY-MM-DD built from the FY label. Using
          // start.toISOString() here would shift 1-Apr local midnight back to
          // 31-Mar (the previous FY) in +0530 zones and mis-bucket the record.
          effectiveDate: `${financialYear.split("-")[0]}-04-01`,
        });
        setCtcValue(0);
        setIsYearly(true);
        setPfEmployeeRate(Math.max(12, companyPfRate));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYear]);

  // ── Salary engine — identical formulas to EmployeeForm's
  // calculateSalaryDetailsForRecord, driven by the current statutory
  // config, declaration, and regime. ──────────────────────────────────
  const details = useMemo(() => {
    const basic = num(record.basicSalary);
    const hra = num(record.hra);
    const conv = num(record.conveyance);
    const med = num(record.medicalallowance);
    const meal = num(record.mealAllowance);
    const special = num(record.allowance);
    const lta = num(record.lta);
    const other = num(record.others);
    const customTotal =
      record.customComponents?.reduce((s, c) => s + (c.amount || 0), 0) || 0;
    // Bonus/Incentive is NOT a salary declaration component — it's added per
    // month from Payroll Records and shown separately on the payslip.
    const gross = basic + hra + conv + med + meal + special + lta + other + customTotal;

    const pfBase = pfBasis === "statutory" ? Math.min(basic, 15000) : basic;
    // Employer fixed 12%; employee can raise their rate (never below 12%).
    const pfEE = pfActive ? Math.round(pfBase * (pfEmployeeRate / 100)) : 0;
    const pfER = pfActive ? Math.round(pfBase * 0.12) : 0;
    const eps = pfActive ? Math.round(Math.min(pfBase, 15000) * 0.0833) : 0;
    const edli = pfActive ? Math.round(Math.min(pfBase, 15000) * 0.005) : 0;

    const esicEE = esicApplicable && !isIntern && gross <= 21000 ? Math.round(gross * 0.0075) : 0;
    const esicER = esicApplicable && !isIntern && gross <= 21000 ? Math.round(gross * 0.0325) : 0;

    const pt = ptApplicable && !isIntern ? calcPT(gross, ptState) : 0;

    const lwfData = lwfMonthly(lwfState);
    const lwfEE = lwfApplicable && !isIntern ? Math.round(lwfData.ee) : 0;
    const lwfER = lwfApplicable && !isIntern ? Math.round(lwfData.er) : 0;

    // Income tax (TDS) is FY-prorated across every component of the year (so a
    // mid-year revision is blended, not annualised on one component). The
    // taxable figure shown is the same FY blend.
    const taxableAnnual = fyTax.annualTaxable;
    const tds = fyTax.monthlyTds;

    // Income tax + PT + LWF make up the monthly statutory tax. Employer PF
    // only reduces take-home when it's included in CTC.
    const employerPfInNet = pfFromCtc ? pfER : 0;
    // Employee PF is deducted from take-home in both regimes. (Taxable income /
    // TDS still excludes employee PF in the new regime — see calcTaxableIncome.)
    const employeePfInNet = pfEE;
    const totDed = employeePfInNet + esicEE + pt + lwfEE + tds + employerPfInNet;
    const net = gross - totDed;
    const ctc = gross + (pfFromCtc ? 0 : pfER) + eps + edli + esicER + lwfER;

    return {
      gross, pfEE, pfER, eps, edli, esicEE, esicER, pt, lwfEE, lwfER, tds,
      totDed, net, ctc, taxableAnnual, employerPfInNet, employeePfInNet,
    };
  }, [
    record, pfBasis, pfApplicable, pfActive, pfEmployeeRate, pfFromCtc, esicApplicable,
    ptApplicable, ptState, lwfApplicable, lwfState, taxRegime, declaration,
    monthlyCtc, fyTax, isIntern,
  ]);

  const floors = salaryFloors(monthlyCtc, num(record.basicSalary));
  const basicBelowFloor = monthlyCtc > 0 && num(record.basicSalary) < floors.minBasic;

  // CTC conservation — the company-set CTC is fixed (read-only). As the
  // employee redistributes pay across components the total must always
  // equal the CTC: raising one component means lowering another. ctcDiff
  // > 0 → money still to allocate; < 0 → over-allocated (exceeds CTC).
  const monthlyCtcRounded = Math.round(monthlyCtc);
  const componentsTotal = Math.round(details.gross);
  const ctcDiff = monthlyCtcRounded - componentsTotal;
  const ctcBalanced = monthlyCtc <= 0 || Math.abs(ctcDiff) <= 1;
  const salaryValid = ctcBalanced && !basicBelowFloor;

  // Global lock — once the declaration is saved, the whole page is read-only
  // and nothing can be changed (including the PF contribution chooser). Before
  // saving, the PF contribution can be changed any number of times.
  const locked = declarationLocked;
  const pfChooserLocked = locked;

  // Earnings breakdown — the individual components that make up gross pay.
  // Shown in the Take Home Summary in place of a single "Gross Salary" line.
  // Only components with a value are listed (custom components included).
  const earningComponents: [string, number][] = [
    ["Basic Salary", num(record.basicSalary)],
    ["HRA", num(record.hra)],
    ["Conveyance", num(record.conveyance)],
    ["Medical Allowance", num(record.medicalallowance)],
    ["Meal Allowance / Food Coupons", num(record.mealAllowance)],
    ["Special Allowance", num(record.allowance)],
    ["LTA", num(record.lta)],
    ["Other Allowances", num(record.others)],
    ...(record.customComponents || []).map(
      (c) => [c.name || "Custom Component", c.amount || 0] as [string, number],
    ),
  ].filter(([, v]) => v > 0);

  // FY-actual annual deductions — summed across the year using whichever
  // component is in force each month, so per-component EPF is respected: months
  // where EPF is opted out add 0, months where it's on add their EPF. This is
  // why a component with EPF OFF still shows the EPF earned in the OTHER (EPF-on)
  // months in the Annual column and the net. Earnings stay monthly × 12.
  const fySum = (f: (r: (typeof monthWiseTax.rows)[number]) => number) =>
    monthWiseTax.rows.reduce((s, r) => s + f(r), 0);
  const fyPfEEAnnual = fySum((r) => r.pfEE);
  const fyEmployerPfAnnual = pfFromCtc ? fySum((r) => r.pfER) : 0;
  const fyEsicEEAnnual = fySum((r) => r.esicEE);
  const fyPtAnnual = fySum((r) => r.pt);
  const fyLwfEEAnnual = fySum((r) => r.lwfEE);
  const fyTdsAnnual = monthWiseTax.totalTax;

  // Take Home Summary rows. Monthly is the CURRENT component (shown as-is, not
  // rounded). Earnings annual = monthly × 12; statutory-deduction annual = the
  // FY-actual total (see fySum above). Net Pay's annual is the SUM of the
  // annual column, so the column always foots to the total shown.
  const takeHomeRows = [
    ...earningComponents.map(([label, v]) => ({ label, m: v, a: Math.round(v * 12) })),
    // Employee PF is deducted from take-home in both regimes, so it's always
    // listed here (employer PF only when part of CTC).
    { label: "EPF (Employee)", m: -details.pfEE, a: -fyPfEEAnnual },
    ...(pfFromCtc ? [{ label: "EPF (Employer)", m: -details.employerPfInNet, a: -fyEmployerPfAnnual }] : []),
    { label: "ESIC (Employee)", m: -details.esicEE, a: -fyEsicEEAnnual },
    { label: "Professional Tax", m: -details.pt, a: -fyPtAnnual },
    { label: "Labour Welfare Fund", m: -details.lwfEE, a: -fyLwfEEAnnual },
    { label: "Income Tax (TDS)", m: -details.tds, a: -fyTdsAnnual },
  ];
  // Monthly net is the raw take-home (kept un-rounded per the monthly rule);
  // annual net is the total of every annual row.
  const takeHomeMonthlyNet = takeHomeRows.reduce((s, r) => s + r.m, 0);
  const takeHomeAnnualNet = takeHomeRows.reduce((s, r) => s + r.a, 0);

  // ── Handlers ─────────────────────────────────────────────────────────
  // Once the declaration is locked (saved) no field may change.
  const setField = (field: keyof SalaryRecord, value: number) => {
    if (locked) return;
    setRecord((prev) => ({ ...prev, [field]: value }));
  };

  const setDecl = (field: keyof Declaration, value: number) => {
    if (locked) return;
    setDeclaration((prev) => ({ ...prev, [field]: value }));
  };

  // Open the PF chooser, seeding it with the current basis + rate.
  const openPfModal = () => {
    if (pfChooserLocked) return;
    setPfBasisDraft(pfBasis);
    setPfRateDraft(Math.max(12, pfEmployeeRate || companyPfRate || 12));
    setPfModalOpen(true);
  };

  // Apply the in-modal selection. "actual" → full Basic at the entered %;
  // "statutory" (capped) → min(Basic, ₹15,000) at the standard 12%. Editable
  // freely until the declaration is saved.
  const applyPfChoice = () => {
    if (pfChooserLocked) return;
    const newBasis = pfBasisDraft;
    const newRate = newBasis === "actual" ? Math.max(12, pfRateDraft || 12) : 12;
    // Confirming a PF contribution is an explicit opt-IN: ensure both the
    // establishment gate and this component's EPF flag are on so the engine
    // actually deducts PF and the Investment 80C/PF box reflects it (otherwise
    // an admin opt-out would keep pfEE — and the box — at 0).
    setPfApplicable(true);
    setPfBasis(newBasis);
    setPfEmployeeRate(newRate);
    setRecord((prev) => ({ ...prev, pfEmployeeRate: newRate, pfApplicable: true }));
    setPfModalOpen(false);
  };

  // Live preview of the HRA exemption from the calculator inputs.
  const hraExemptionPreview = paysRent
    ? calcHraExemption(
        num(record.basicSalary) * 12,
        annualHraReceived,
        annualRent,
        isMetro,
      )
    : 0;

  // Open the calculator, seeding "HRA received" from the current HRA
  // component (×12) and any previously-claimed exemption.
  const openHraCalc = () => {
    setAnnualHraReceived(num(record.hra) * 12);
    setHraCalcOpen(true);
  };

  // LTA exemption = lowest of the actual travel expense and the LTA received
  // from the employer (component × 12). Live preview drives declaration.ltaExempt.
  const ltaReceivedAnnual = num(record.lta) * 12;
  const ltaExemptionPreview = calcLtaExemption(travelExpense, ltaReceivedAnnual);

  // Open the LTA claim. Keep the previously-entered travel expense (so it shows
  // as typed, e.g. 60,000, even though the exemption is capped at the LTA
  // received); only fall back to the saved exemption when nothing's entered yet.
  const openLtaCalc = () => {
    setTravelExpense((prev) => (prev > 0 ? prev : num(declaration.ltaExempt)));
    setLtaCalcOpen(true);
  };

  // ── Persisting the declaration ───────────────────────────────────────
  // Build and send the full save payload (every salary component + the tax
  // config). `lock` finalises the declaration: it locks the PF rate on the
  // current component and flags the whole declaration read-only. The lightweight
  // exemption save passes lock=false so an applied HRA/LTA exemption is written
  // to the server right away while the rest of the declaration stays editable.
  const saveDeclaration = async (decl: Declaration, lock: boolean) => {
    if (!employeeId) return;
    // Send EVERY component (all FYs) — the backend replaces the whole salary
    // set on save, so omitting other years would wipe them. The edited current
    // record is substituted into the full set; older components and other FYs
    // are preserved as-is. A finalising save also locks the PF rate.
    const currentForSave: SalaryRecord = {
      ...record,
      pfEmployeeRate: Math.max(12, pfEmployeeRate || 12),
      pfRateLocked: lock,
      // A finalising save locks ONLY this component; once locked it stays locked.
      declarationLocked: lock || record.declarationLocked,
    };
    const hasCurrent = allRecords.some((r) => r.id === record.id);
    const rawSet: SalaryRecord[] = hasCurrent
      ? allRecords.map((r) => (r.id === record.id ? currentForSave : r))
      : [...allRecords, currentForSave];

    // PF rate is PER COMPONENT — each component keeps its OWN rate + lock. Only
    // the component being edited gets the freshly-chosen rate (via
    // currentForSave); already-saved components are preserved verbatim. This is
    // what stops locking a new component (e.g. at 14%) from retroactively
    // changing an older locked component's PF % (e.g. 12% → 14%).
    const fullSet: SalaryRecord[] = rawSet;

    // Monthly TDS computed per financial year (each FY's components blended).
    const groups: Record<string, SalaryRecord[]> = {};
    fullSet.forEach((r) => { (groups[fyOfRec(r)] ||= []).push(r); });
    const tdsByFy: Record<string, number> = {};
    const endDateById: Record<string, string | null> = {};
    Object.entries(groups).forEach(([fy, recs]) => {
      const sorted = [...recs].sort(byEffectiveDateAsc);
      const { start, end } = fyBounds(fy);
      const slices: FySlice[] = sorted.map((r, i) => {
        const m = monthlyOf(r);
        const eff = r.effectiveDate ? new Date(r.effectiveDate) : null;
        const next = sorted[i + 1];
        let effEnd: Date | null = null;
        if (next?.effectiveDate) { effEnd = new Date(next.effectiveDate); effEnd.setDate(effEnd.getDate() - 1); }
        endDateById[r.id] = effEnd ? effEnd.toISOString().split("T")[0] : null;
        return { monthlyGross: m.gross, months: monthsActiveInFy(eff, effEnd, start, end), pfEEMonthly: m.pfEE, pfERMonthly: m.pfER, mealVoucherMonthly: m.mealVoucher };
      });
      // Use the passed `decl` so a just-applied exemption is reflected even
      // before component state has re-rendered.
      const base = calcFyTax(slices, taxRegime, decl, { taxableIncome: 0, tdsPaid: 0 });
      tdsByFy[fy] = (tdsApplicable && !isIntern) ? base.monthlyTds : 0;
    });

    // Every component except the newest is superseded — a later one has taken
    // over, so it is a closed slice and its declaration is locked. Derived
    // across ALL financial years (endDateById above is per-FY, so the last
    // component of an older year has no end_date even though a newer year's
    // component replaced it) and written into the payload, so the stored flag
    // matches what both screens already show instead of the lock only landing
    // on the following save.
    // The component on screen is never force-locked here — only an explicit
    // "Save Declaration" (lock=true) closes it.
    const supersededIds = new Set(
      [...fullSet].sort(byEffectiveDateAsc).slice(0, -1)
        .map((r) => r.id)
        .filter((id) => id !== record.id),
    );

    // Top-level summary mirrors the current component's freshly computed values
    // (so a lock=false exemption save isn't held back by a stale `details`).
    let curNet = details.net, curGross = details.gross, curTds = details.tds;
    const salaryDetailsArray = fullSet.map((r) => {
      const m = monthlyOf(r);
      const fy = fyOfRec(r);
      const tds = tdsByFy[fy] || 0;
      const employerPfInNet = pfFromCtc ? m.pfER : 0;
      // Employee PF is deducted from take-home in both regimes.
      const employeePfInNet = m.pfEE;
      const net = m.gross - (employeePfInNet + m.esicEE + m.pt + m.lwfEE + tds + employerPfInNet);
      const isCurrent = r.id === record.id;
      if (isCurrent) { curNet = net; curGross = m.gross; curTds = tds; }
      return {
        basicsalary: r.basicSalary,
        hra: r.hra,
        conveyance: r.conveyance,
        medicalallowance: r.medicalallowance,
        mealallowance: r.mealAllowance,
        meal_allowance_type: r.mealAllowanceType || "cash",
        allowance: r.allowance,
        lta: r.lta,
        others: r.others,
        bonus: 0, // bonus is a per-month Payroll Records item, not a declaration component
        // Preserve the stored CTC; for the current component use the shown CTC.
        ctc: isCurrent ? Math.round(isYearly ? ctcValue : ctcValue * 12) : Math.round(num(r.ctc) || m.gross * 12),
        pf: m.pfEE,
        esi: m.esicEE,
        proftax: m.pt,
        labourwelfare: m.lwfEE,
        tds,
        netsalary: net,
        effectiveDate: r.effectiveDate,
        effective_date: r.effectiveDate || null,
        end_date: endDateById[r.id] || null,
        financial_year: fy,
        pf_employee_rate: Math.max(12, num(r.pfEmployeeRate) || 12),
        pf_rate_locked: !!r.pfRateLocked,
        // Effective per-component EPF flag (mandatory ON at/below ₹15,000 basic).
        pf_applicable: recordPfOn(r),
        // Per-component declaration lock — each component keeps its OWN flag (NOT
        // FY-normalised like pf_rate_locked), so a new component stays open. A
        // superseded component is locked whether or not it was ever declared.
        declaration_locked: !!r.declarationLocked || supersededIds.has(r.id),
        status: "active",
        customComponents: r.customComponents,
      };
    });

    const payload: any = {
      id: employeeId,
      SalaryDetails: salaryDetailsArray,
      TaxConfig: [
        {
          pfApplicable, esicApplicable, ptApplicable, lwfApplicable, tdsApplicable,
          pfBasis, pfEmployeeRate, ptState, lwfState, taxRegime,
          // Declaration lock flag (only a finalising save locks).
          declarationLocked: lock,
          // EPF off-state for the current component + optional identifiers
          // (UAN / PF member ID) persisted for the admin view.
          pfOptOut: currentPfOff, uan, pfMemberId,
          ...decl,
        },
      ],
      basicsalary: record.basicSalary,
      hra: record.hra,
      conveyance: record.conveyance,
      medicalallowance: record.medicalallowance,
      mealallowance: record.mealAllowance,
      allowance: record.allowance,
      lta: record.lta,
      others: record.others,
      bonus: 0, // bonus is a per-month Payroll Records item, not a declaration component
      netsalary: curNet,
      salary_amount: curGross,
      tds: curTds,
      pfApplicable,
      esicApplicable,
      ptApplicable,
      lwfApplicable,
      pfBasis,
      pfEmployeeRate,
      ptState,
      lwfState,
      taxRegime,
      pfOptOut: currentPfOff,
      uan,
      pfMemberId,
      ...decl,
    };

    await updateEmployee(employeeId, payload);
    // Server now holds it (salary + PF + declaration), whether or not this was a
    // locking save — drop the local drafts so a reload reflects the saved values,
    // not a stale draft.
    try {
      localStorage.removeItem(DECL_DRAFT_KEY(employeeId));
      localStorage.removeItem(SALARY_DRAFT_KEY(employeeId));
    } catch {
      /* ignore */
    }
  };

  // Persist an applied HRA/LTA exemption to the server immediately (without
  // locking the declaration). When the salary isn't in a saveable state the
  // local draft still protects the value until the next full save.
  const persistExemption = async (decl: Declaration) => {
    if (!employeeId || locked) return;
    if (!salaryValid || (pfActive && pfEmployeeRate < 12)) {
      toast({
        title: "Exemption applied",
        description: "Balance your salary components and Save Declaration to store it on the server.",
      });
      return;
    }
    setSaving(true);
    try {
      await saveDeclaration(decl, false);
      toast({ title: "Exemption saved" });
    } catch (err: any) {
      toast({
        title: "Couldn't save exemption",
        description:
          err?.response?.data?.detail || err?.message ||
          "It's kept on this device and will be saved with your declaration.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const applyHraExemption = () => {
    const next: Declaration = { ...declaration, hraExempt: hraExemptionPreview };
    setDeclaration(next);
    setHraCalcOpen(false);
    void persistExemption(next);
  };

  const applyLtaExemption = () => {
    const next: Declaration = { ...declaration, ltaExempt: ltaExemptionPreview };
    setDeclaration(next);
    setLtaCalcOpen(false);
    void persistExemption(next);
  };

  const handleSave = async () => {
    if (!employeeId) return;
    // Already submitted — the declaration is locked and can't be saved again.
    if (locked) return;
    if (pfActive && pfEmployeeRate < 12) {
      toast({
        title: "PF contribution too low",
        description: "Your PF contribution can't be below 12%.",
        variant: "destructive",
      });
      return;
    }
    if (!salaryValid) {
      toast({
        title: "Balance your salary components",
        description: !ctcBalanced
          ? `Your components total ₹${fmt(componentsTotal)} but your CTC is ₹${fmt(monthlyCtcRounded)}. Adjust a component so they match.`
          : `Basic must be ≥ ₹${fmt(floors.minBasic)} (50% of CTC).`,
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      await saveDeclaration(declaration, true);
      // Lock ONLY the component just declared — keep local state in step so the
      // page goes read-only without a reload, while other components are
      // untouched. A future admin-added increment will load unlocked.
      setDeclarationLocked(true);
      setRecord((prev) => ({ ...prev, declarationLocked: true }));
      setAllRecords((prev) =>
        prev.map((r) => (r.id === record.id ? { ...r, declarationLocked: true } : r)),
      );
      toast({
        title: "Declaration saved",
        description: "This component's declaration is now locked. Any new component your admin adds will be editable until you save it too.",
      });
    } catch (err: any) {
      toast({
        title: "Couldn't save declaration",
        description:
          err?.response?.data?.detail || err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your salary details…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-xl mx-auto mt-10 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
        <p className="font-semibold text-red-700 mb-1">{loadError}</p>
        <p className="text-sm text-red-500">
          Please contact HR if your payroll profile hasn't been set up yet.
        </p>
      </div>
    );
  }

  const regimeToggle = (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
      {(["new", "old"] as const).map((r) => (
        <button
          key={r}
          type="button"
          disabled={locked}
          onClick={() => {
            if (locked) return;
            r === "old" ? setOldRegimeNotice(true) : setTaxRegime(r);
          }}
          className={`px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
            taxRegime === r
              ? "bg-blue-600 text-white"
              : "bg-white text-gray-600 hover:bg-gray-50"
          }`}
        >
          {r === "new" ? "New Regime" : "Old Regime"}
        </button>
      ))}
    </div>
  );

  // Read-only field + grid for older (locked) components.
  const roField = (label: string, value: number) => (
    <div>
      <Label className="text-xs font-semibold text-gray-500">{label}</Label>
      <div className="mt-1 h-10 px-3 flex items-center rounded-md border bg-gray-100 text-gray-600 text-sm">
        ₹{fmt(value)}
      </div>
    </div>
  );
  const readOnlyComponentGrid = (r: SalaryRecord) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {roField("Basic Salary", num(r.basicSalary))}
      {roField("HRA (House Rent Allowance)", num(r.hra))}
      {roField("Conveyance Allowance", num(r.conveyance))}
      {roField("Medical Allowance", num(r.medicalallowance))}
      {roField("Meal Allowance / Food Coupons", num(r.mealAllowance))}
      {roField("Special Allowance", num(r.allowance))}
      {roField("LTA (Leave Travel Allowance)", num(r.lta))}
      {roField("Other Allowances", num(r.others))}
    </div>
  );

  const componentField = (
    label: string,
    field: keyof SalaryRecord,
    opts?: { required?: boolean; help?: string; helpError?: boolean; extra?: React.ReactNode },
  ) => (
    <div>
      <Label className="text-xs font-semibold text-gray-500">
        {label} {opts?.required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        type="number"
        disabled={locked}
        value={(record[field] as number) === 0 ? "" : (record[field] as number)}
        onChange={(e) => setField(field, e.target.value === "" ? 0 : Number(e.target.value))}
        className={`mt-1 ${opts?.helpError ? "border-red-400 focus-visible:ring-red-400" : ""} disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed`}
        placeholder="0"
      />
      {opts?.help && (
        <p className={`text-xs mt-1 ${opts.helpError ? "text-red-500" : "text-gray-400"}`}>
          {opts.help}
        </p>
      )}
      {opts?.extra}
    </div>
  );

  const deductionField = (
    label: string,
    field: keyof Declaration,
    tip: string,
    opts?: { readOnly?: boolean; value?: number; limit?: string; newRegimeBadge?: boolean },
  ) => (
    <div>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1">
          <Label className="text-sm font-medium text-gray-700">{label}</Label>
          <span title={tip} className="cursor-help text-gray-400">
            <Info className="w-3.5 h-3.5" />
          </span>
        </div>
        {/* Green new-regime badge — shown only on the NPS fields that remain
            deductible under the new regime. */}
        {opts?.newRegimeBadge && taxRegime === "new" && (
          <span className="shrink-0 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5">
            Allowed in New Regime
          </span>
        )}
      </div>
      <div className={`flex items-stretch rounded-lg border border-gray-300 overflow-hidden ${opts?.readOnly ? "bg-gray-100" : "bg-white"}`}>
        <span className="px-3 flex items-center bg-gray-50 text-gray-500 border-r border-gray-200">₹</span>
        {opts?.readOnly ? (
          <input
            type="number"
            value={opts.value ?? 0}
            readOnly
            disabled
            className="flex-1 px-3 py-2 outline-none text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        ) : (
          <input
            type="number"
            disabled={locked}
            value={declaration[field] === 0 ? "" : declaration[field]}
            onChange={(e) => setDecl(field, e.target.value === "" ? 0 : Number(e.target.value))}
            placeholder={opts?.limit || "0"}
            className="flex-1 px-3 py-2 outline-none text-sm disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap md:flex-nowrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Tax Summary</h1>
          <p className="text-sm text-gray-500">
            {employeeName ? `${employeeName} · ` : ""}Declare your salary structure and tax-saving investments.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Financial year — pinned in the header, just left of Save. */}
          <Select value={financialYear} onValueChange={setFinancialYear}>
            <SelectTrigger className="w-40 h-10 border-emerald-300 bg-emerald-50/70 text-sm font-medium text-emerald-800">
              <SelectValue placeholder="Select FY" />
            </SelectTrigger>
            <SelectContent>
              {/* Only the running FY and the previous one (plus the currently
                  selected year, in case stored data points to an older FY). */}
              {Array.from(new Set([...financialYearOptions(2), financialYear]))
                .sort((a, b) => (a < b ? 1 : -1))
                .map((fy) => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setBankModalOpen(true)}
            disabled={!employeeId}
            className="h-10 border-blue-300 bg-blue-50/70 text-blue-700 hover:bg-blue-100"
          >
            <Landmark className="w-4 h-4 mr-2" />
            Bank Details
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || locked || !salaryValid || (pfActive && pfEmployeeRate < 12)}
            title={locked ? "Declaration saved — locked" : !salaryValid ? "Balance your salary components before saving" : undefined}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {locked ? "Declaration Locked" : "Save Declaration"}
          </Button>
        </div>
      </div>

      <BankDetailsModal
        open={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        employeeId={employeeId}
        subtitle={employeeName ? `${employeeName} — used to credit your salary.` : undefined}
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
        {/* Interns have no tax-saving investments (no TDS at all), so the
            Investment tab is hidden and the list collapses to two columns. */}
        <TabsList className={`grid w-full ${isIntern ? "grid-cols-2" : "grid-cols-3"} h-auto p-1`}>
          <TabsTrigger value="salary" className="py-2.5">
            <Wallet className="w-4 h-4 mr-1.5" /> Salary
          </TabsTrigger>
          {!isIntern && (
            <TabsTrigger value="investment" className="py-2.5">
              <PiggyBank className="w-4 h-4 mr-1.5" /> Investment
            </TabsTrigger>
          )}
          <TabsTrigger value="earnings" className="py-2.5">
            <Calculator className="w-4 h-4 mr-1.5" /> Take Home Summary
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Salary Declaration ─────────────────────────────── */}
        <TabsContent value="salary">
          <Card className="border shadow-sm">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg">Salary Declaration</CardTitle>
                  <CardDescription>
                    Your CTC is set by the company and can't be changed. You can redistribute pay across
                    components — Basic must stay ≥ 50% of CTC, and the components must always add up to
                    your CTC.
                  </CardDescription>
                </div>
                {/* Per-component EPF — when this component's Basic is above the
                    ₹15,000 ceiling the employee can turn EPF on/off for it.
                    At/below ₹15,000 EPF is mandatory, so the toggle is hidden.
                    Turning it on opens the chooser (Actual vs Capped). Hidden
                    entirely for interns — they have no PF at all. */}
                {!isIntern && (
                <div className="shrink-0 text-right space-y-1.5">
                  {canToggleCurrentPf && (locked ? (
                    // Declaration saved → the EPF choice is FINAL. Shown
                    // read-only so it's clear it can no longer be changed.
                    <div
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                        currentPfOff
                          ? "border-red-300 bg-red-50 text-red-600"
                          : "border-emerald-300 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      🔒 EPF {currentPfOff ? "Off" : "On"} · locked
                    </div>
                  ) : (
                    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          // Opting EPF on must also lift the establishment-level
                          // gate. recordPfOn()/details.pfEE are gated by the
                          // establishment `pfApplicable`; if the admin had opted
                          // this employee out (pfApplicable=false), flipping only
                          // the per-component flag leaves PF at 0 (the Investment
                          // PF box stays 0 even after opting in).
                          setPfApplicable(true);
                          setRecord((prev) => ({ ...prev, pfApplicable: true }));
                          openPfModal();
                        }}
                        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                          !currentPfOff ? "bg-emerald-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        EPF On
                      </button>
                      <button
                        type="button"
                        onClick={() => setRecord((prev) => ({ ...prev, pfApplicable: false }))}
                        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                          currentPfOff ? "bg-red-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        EPF Off
                      </button>
                    </div>
                  ))}
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!pfApplicable || currentPfOff || pfChooserLocked}
                      onClick={openPfModal}
                      className={`disabled:opacity-60 disabled:cursor-not-allowed ${
                        currentPfOff
                          ? "border-red-300 text-red-600 bg-red-50"
                          : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      }`}
                    >
                      <PiggyBank className="w-4 h-4 mr-1.5" />
                      {currentPfOff
                        ? "EPF — Off for this component"
                        : `Set PF Contribution · ${pfBasis === "actual"
                            ? `Actual ${Math.max(12, pfEmployeeRate || 12)}%`
                            : "Capped 12%"}`}
                    </Button>
                    <p className={`text-[11px] mt-1 ${currentPfOff ? "text-red-500" : pfChooserLocked ? "text-amber-600" : "text-gray-400"}`}>
                      {currentPfOff
                        ? "EPF is off for this component — no PF will be deducted for its months."
                        : locked
                        ? "Declaration saved — PF locked"
                        : "Choose Actual or Capped basis"}
                    </p>
                  </div>
                </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              {!fyHasComponents && !isCarriedForward && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-700">
                  No salary components recorded for FY {financialYear}.
                </div>
              )}
              {isCarriedForward && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">
                  Salary carried forward from your previous component. Save the declaration to set it for FY {financialYear}.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border">
                <div>
                  <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    CTC <span className="text-gray-400 normal-case">(set by company)</span>
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="number"
                      value={ctcValue || ""}
                      readOnly
                      disabled
                      className="flex-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                    <div className="w-24 flex items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-sm text-gray-500">
                      {isYearly ? "Yearly" : "Monthly"}
                    </div>
                  </div>
                  {monthlyCtc > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Monthly CTC: ₹{fmt(monthlyCtc)} · Net pay: ₹{fmt(Math.round(details.net))}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Effective Date <span className="text-gray-400 normal-case">(set by company)</span>
                  </Label>
                  <Input
                    type="date"
                    value={record.effectiveDate}
                    readOnly
                    disabled
                    className="mt-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Current (most recent) component — editable, shown on top. */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Current Salary Component</span>
                <span className="text-[11px] text-blue-600">editable</span>
                <span className="text-xs text-gray-500">· Effective {record.effectiveDate} → present</span>
              </div>

              {/* CTC conservation banner — total of components must equal CTC. */}
              {monthlyCtc > 0 && (
                <div
                  className={`flex items-center justify-between flex-wrap gap-2 rounded-lg border px-4 py-2.5 text-sm ${
                    ctcBalanced
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-red-200 bg-red-50 text-red-700"
                  }`}
                >
                  <span>
                    Allocated <span className="font-semibold">₹{fmt(details.gross)}</span> of{" "}
                    <span className="font-semibold">₹{fmt(monthlyCtc)}</span> monthly CTC
                  </span>
                  <span className="font-medium">
                    {ctcBalanced
                      ? "Balanced ✓"
                      : ctcDiff > 0
                      ? `₹${fmt(ctcDiff)} unallocated — increase another component`
                      : `Exceeds CTC by ₹${fmt(Math.abs(ctcDiff))} — reduce another component`}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {componentField("Basic Salary", "basicSalary", {
                  required: true,
                  help: basicBelowFloor
                    ? `Below minimum — Basic must be ≥ ₹${fmt(floors.minBasic)} (50% of CTC)`
                    : monthlyCtc > 0
                    ? `Min ₹${fmt(floors.minBasic)} (50% of CTC)`
                    : undefined,
                  helpError: basicBelowFloor,
                })}
                {componentField("HRA (House Rent Allowance)", "hra", {
                  extra: (
                    <button
                      type="button"
                      disabled={locked}
                      onClick={openHraCalc}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Home className="w-3.5 h-3.5" />
                      {declaration.hraExempt > 0
                        ? `HRA exemption: ₹${fmt(declaration.hraExempt)}/yr — edit`
                        : "Claim HRA exemption (pay rent?)"}
                    </button>
                  ),
                })}
                {componentField("Conveyance Allowance", "conveyance")}
                {componentField("Medical Allowance", "medicalallowance")}
                {componentField("Meal Allowance / Food Coupons", "mealAllowance", {
                  extra: (
                    <div className="mt-1.5 flex items-center gap-4">
                      {(["voucher", "cash"] as const).map((opt) => (
                        <label
                          key={opt}
                          className={`flex items-center gap-1.5 text-xs ${locked ? "text-gray-400 cursor-not-allowed" : "text-gray-600 cursor-pointer"}`}
                        >
                          <input
                            type="checkbox"
                            disabled={locked}
                            checked={(record.mealAllowanceType || "cash") === opt}
                            onChange={() =>
                              !locked && setRecord((p) => ({ ...p, mealAllowanceType: opt }))
                            }
                            className="h-3.5 w-3.5 rounded text-emerald-600"
                          />
                          {opt === "voucher" ? "Voucher" : "Cash"}
                        </label>
                      ))}
                    </div>
                  ),
                })}
                {componentField("Special Allowance", "allowance")}
                {componentField("LTA (Leave Travel Allowance)", "lta", {
                  extra: (
                    <button
                      type="button"
                      disabled={locked}
                      onClick={openLtaCalc}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Plane className="w-3.5 h-3.5" />
                      {declaration.ltaExempt > 0
                        ? `LTA exemption: ₹${fmt(declaration.ltaExempt)}/yr — edit`
                        : "Claim LTA exemption (travelled?)"}
                    </button>
                  ),
                })}
                {componentField("Other Allowances", "others")}
              </div>

              <div className="flex justify-end gap-4 text-sm pt-2 border-t">
                <span className="text-gray-500">
                  Gross / month:{" "}
                  <span className="font-bold text-gray-900">₹{fmt(details.gross)}</span>
                </span>
              </div>

              {/* Previous components — read-only, collapsed by default, shown
                  below the current one (most recent first). */}
              {olderRecords.length > 0 && (
                <div className="space-y-3 pt-2 border-t">
                  <p className="text-sm font-semibold text-gray-700">Previous Components (locked)</p>
                  {[...olderRecords].reverse().map((r) => {
                    const isOpen = expandedEmpIds.has(r.id);
                    // End = day before the next component's effective date.
                    const idx = visibleRecords.findIndex((v) => v.id === r.id);
                    const next = visibleRecords[idx + 1];
                    let end = "present";
                    if (next?.effectiveDate) {
                      const d = new Date(next.effectiveDate);
                      d.setDate(d.getDate() - 1);
                      end = d.toISOString().split("T")[0];
                    }
                    return (
                      <div key={r.id} className="rounded-lg border bg-gray-50/60">
                        <button
                          type="button"
                          onClick={() => toggleEmpExpanded(r.id)}
                          className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-left"
                        >
                          <span className="text-gray-400 text-xs">{isOpen ? "▲" : "▼"}</span>
                          <span className="text-sm font-medium text-gray-700">Salary Component</span>
                          <span className="text-[11px] font-medium text-amber-600">Locked</span>
                          <span className="text-xs text-gray-500">Effective: {r.effectiveDate} → {end}</span>
                          <span className="text-xs text-gray-500">CTC: ₹{fmt(num(r.ctc))}</span>
                          {/* Show the PF rate only when EPF is actually applicable for
                              this component (matches monthlyOf/the expanded grid, which
                              gate on recordPfOn). When EPF is opted out — establishment
                              off, or this component opted out above the ₹15,000 ceiling —
                              show "Off" instead of a misleading 12%. */}
                          <span className="text-xs text-gray-500">
                            PF: {recordPfOn(r) ? `${Math.max(12, num(r.pfEmployeeRate) || 12)}%` : "Off"}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4">
                            {readOnlyComponentGrid(r)}
                            <div className="flex justify-end mt-2 text-sm text-gray-500">
                              Gross / month: <span className="font-bold text-gray-800 ml-1">₹{fmt(monthlyOf(r).gross)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          <div className="flex justify-end mt-4">
            {/* Interns skip the Investment tax step — jump straight to the
                Take Home Summary instead. */}
            {isIntern ? (
              <Button onClick={() => goToTab("earnings")} className="bg-blue-600 hover:bg-blue-700 text-white">
                Next: Take Home Summary
              </Button>
            ) : (
              <Button onClick={() => goToTab("investment")} className="bg-blue-600 hover:bg-blue-700 text-white">
                Next: Investment Declaration
              </Button>
            )}
          </div>
        </TabsContent>

        {/* ── Tab 2: Investment Declaration ─────────────────────────── */}
        <TabsContent value="investment">
          <Card className="border shadow-sm">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50">
              <CardTitle className="text-lg">Investment Declaration</CardTitle>
              <CardDescription>
                Declare your tax-saving investments and deductions. Pick your tax regime in the
                Earnings tab — which of these apply depends on the regime you choose there.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {deductionField("Employee's contribution to NPS – 80CCD", "sec80ccd1b", "Auto-filled from your PF contribution % and CTC on the Salary tab (annual). Max ₹50,000 under 80CCD(1B).", { readOnly: true, value: Math.round(details.pfEE * 12), limit: "Max ₹50,000" })}
                {deductionField("Employer's contribution to NPS – 80CCD(2)", "npsEmployer", "Auto-filled from the employer's PF contribution and CTC on the Salary tab (annual). Up to 10% of Basic (14% for govt).", { readOnly: true, value: Math.round(details.pfER * 12), limit: "Up to 10% of Basic", newRegimeBadge: true })}
                {deductionField("Basic deductions – 80C", "sec80c", "PPF, ELSS, LIC, EPF, tuition fees, etc. Max ₹1,50,000.", { limit: "Max ₹1,50,000" })}
                {deductionField("Interest from deposits – 80TTA", "sec80tta", "Interest from savings bank deposits. Max ₹10,000.", { limit: "Max ₹10,000" })}
                {deductionField("Medical insurance – 80D", "sec80d", "self + family max ₹25,000, parents max ₹25,000 if aged < 60, ₹50,000 if aged > 60.", { limit: "Max ₹50,000–₹75,000" })}
                {deductionField("Donations to charity – 80G", "sec80g", "Eligible donations to approved funds/charities.", { limit: "As eligible (50%/100%)" })}
                {deductionField("Interest on housing loan – 80EE/80EEA", "sec80eea", "Additional interest on first-time home loan. Max ₹1,50,000.", { limit: "Max ₹1,50,000" })}
                {deductionField("Interest on education loan – 80E", "sec80e", "Interest on loan taken for education claimed for 8 consecutive assessment years.", { limit: "As eligible (no cap)" })}
                {deductionField("Disability benefits – 80U/80DD", "sec80u", "Max ₹75,000 in case of normal disability and ₹1,25,000 in severe disability.", { limit: "Max ₹75,000–₹1,25,000" })}
                {deductionField("Any other deduction", "otherDed", "Any other eligible deduction not listed above.", { limit: "As eligible" })}
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end mt-4">
            <Button onClick={() => goToTab("earnings")} className="bg-blue-600 hover:bg-blue-700 text-white">
              Next: Take Home Summary
            </Button>
          </div>
        </TabsContent>

        {/* ── Tab 3: Earnings ───────────────────────────────────────── */}
        <TabsContent value="earnings">
          <Card className="border shadow-sm">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="text-lg">Take Home Summary</CardTitle>
              <CardDescription>
                Computed from your salary components and investment declaration. Switch the tax regime to
                compare take-home pay.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <Label className="font-medium text-gray-700">Tax Regime:</Label>
                {regimeToggle}
              </div>

              <div className="rounded-lg border overflow-hidden">
                <div className="grid grid-cols-3 px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm">
                  <span>Take Home Summary</span>
                  <span className="text-right">Monthly</span>
                  <span className="text-right">Annual</span>
                </div>
                <div className="divide-y text-sm">
                  {takeHomeRows.map(({ label, m, a }) => (
                    <div key={label} className="grid grid-cols-3 px-4 py-2">
                      <span className="text-gray-600">{label}</span>
                      <span className={`text-right font-medium ${m < 0 ? "text-red-600" : "text-gray-900"}`}>
                        {m < 0 ? "-" : ""}₹{fmt(Math.abs(m))}
                      </span>
                      <span className={`text-right font-medium ${a < 0 ? "text-red-600" : "text-gray-900"}`}>
                        {a < 0 ? "-" : ""}₹{fmt(Math.abs(a))}
                      </span>
                    </div>
                  ))}
                  <div className="grid grid-cols-3 px-4 py-3 bg-blue-50">
                    <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                    <span className="text-right font-bold text-blue-700">₹{fmt(takeHomeMonthlyNet)}</span>
                    <span className="text-right font-bold text-blue-700">₹{fmt(takeHomeAnnualNet)}</span>
                  </div>
                </div>
              </div>

              {/* Month-wise (slab) breakdown — EPF and income tax vary by month;
                  the year's tax is charged only in the months the salary is
                  above ₹12,00,000. Collapsed by default. */}
              <button
                type="button"
                onClick={() => setShowSlabTable((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700"
              >
                <span className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-purple-600" />
                  {showSlabTable ? "Hide" : "View"} salary periods &amp; month-wise tax
                </span>
                <span className="text-gray-400">{showSlabTable ? "▲" : "▼"}</span>
              </button>

              {showSlabTable && (
              <div className="rounded-lg border overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm flex items-center justify-between">
                  <span>Salary periods &amp; tax (FY {financialYear})</span>
                  <span className="text-xs font-normal text-gray-500">
                    {monthWiseTax.highMonthCount > 0
                      ? `Tax charged in ${monthWiseTax.taxMonthCount} month(s) above ₹12L`
                      : monthWiseTax.totalTax > 0
                      ? `Tax spread over ${monthWiseTax.taxMonthCount} working month(s)`
                      : "No income tax this year (within rebate)"}
                  </span>
                </div>
                <div className="grid px-4 py-2 bg-gray-50/60 text-xs font-semibold text-gray-600 border-t" style={{ gridTemplateColumns: slabColTemplate }}>
                  <span>Period</span>
                  {slabNumCols.map((c) => (
                    <span key={c.header} className="text-right">{c.header}</span>
                  ))}
                </div>
                <div className="divide-y text-sm">
                  {monthWiseTax.periods.map((p, i) => {
                    const range = p.fromLabel === p.toLabel ? p.fromLabel : `${p.fromLabel} – ${p.toLabel}`;
                    return (
                      <div
                        key={`${p.fromLabel}-${i}`}
                        className={`grid px-4 py-2.5 ${!p.active ? "opacity-50" : ""} ${p.taxTotal > 0 ? "bg-amber-50/50" : ""}`}
                        style={{ gridTemplateColumns: slabColTemplate }}
                      >
                        <span className="text-gray-700">
                          {range}
                          <span className="text-[11px] text-gray-400"> · {p.months} mo</span>
                          {p.isHigh && <span className="ml-1 text-[10px] text-amber-600">&gt;₹12L</span>}
                        </span>
                        {slabNumCols.map((c) => {
                          let cls = "text-gray-800";
                          if (c.tone === "ded") cls = "text-red-600";
                          else if (c.tone === "net") cls = "font-semibold text-gray-900";
                          else if (c.tone === "tax") cls = p.taxTotal > 0 ? "font-medium text-amber-700" : "text-gray-400";
                          return (
                            <span key={c.header} className={`text-right ${cls}`}>₹{fmt(c.cell(p))}</span>
                          );
                        })}
                      </div>
                    );
                  })}
                  <div className="grid px-4 py-2.5 bg-gray-50 font-semibold text-gray-800 text-sm" style={{ gridTemplateColumns: slabColTemplate }}>
                    <span>Total (year)</span>
                    {slabNumCols.map((c) => {
                      let cls = "";
                      if (c.tone === "ded") cls = "text-red-600";
                      else if (c.tone === "tax") cls = "text-amber-700";
                      return (
                        <span key={c.header} className={`text-right ${cls}`}>₹{fmt(c.total)}</span>
                      );
                    })}
                  </div>
                </div>
              </div>
              )}

              {/* Tax computation — collapsed behind a button by default. */}
              <button
                type="button"
                onClick={() => setShowTaxBreakdown((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700"
              >
                <span className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-purple-600" />
                  {showTaxBreakdown ? "Hide" : "View"} tax computation
                </span>
                <span className="text-gray-400">{showTaxBreakdown ? "▲" : "▼"}</span>
              </button>

              {showTaxBreakdown && (() => {
                // FY-blended annual gross (sum of each component's gross for
                // the months it was in force) so the breakdown matches the
                // prorated tax. Falls back to the current CTC ×12.
                const grossAnnual = fyTax.annualGross > 0
                  ? fyTax.annualGross
                  : (monthlyCtc > 0 ? Math.round(monthlyCtc * 12) : details.gross * 12);
                const empPfAnnual = details.pfEE * 12;
                const employerPfAnnual = details.pfER * 12;
                // Standard deduction can't exceed the income it's applied to —
                // when the annual gross is below the statutory cap, the whole
                // gross becomes the deduction (matches calcTaxableIncome).
                const stdDeduction = Math.min(
                  taxRegime === "new" ? 75000 : 50000,
                  Math.max(0, grossAnnual),
                );
                // New regime: the employee's own PF doesn't reduce taxable
                // income, so it's excluded from the build (and the residual).
                const empPfDeductible = taxRegime === "new" ? 0 : empPfAnnual;
                // Meal-voucher exemption (BOTH regimes), capped — FY total of the
                // voucher-paid meal allowance from the in-force component. Taken
                // off gross before the standard deduction (matches calcTaxableIncome).
                const mealVoucherExempt = Math.min(
                  monthWiseTax.rows.reduce((s, r) => s + (r.mealVoucher || 0), 0),
                  MEAL_VOUCHER_ANNUAL_CAP,
                );
                const otherDeductions = Math.max(
                  0,
                  grossAnnual - empPfDeductible - employerPfAnnual - stdDeduction - mealVoucherExempt - details.taxableAnnual,
                );
                const incomeTaxAnnual = details.tds * 12;
                const ptAnnual = details.pt * 12;
                const lwfAnnual = details.lwfEE * 12;
                const totalAnnualTax = incomeTaxAnnual + ptAnnual + lwfAnnual;
                const rows: [string, number, boolean?][] = [
                  ["Annual gross income", grossAnnual],
                  // Employee PF only reduces taxable income in the old regime.
                  ...(taxRegime === "new"
                    ? []
                    : ([["− Employee PF", -empPfAnnual]] as [string, number, boolean?][])),
                  ["− Employer PF", -employerPfAnnual],
                  // Meal-voucher exemption comes off BEFORE the standard deduction.
                  ...(mealVoucherExempt > 0
                    ? ([["− Meal voucher exemption", -mealVoucherExempt]] as [string, number, boolean?][])
                    : []),
                  [`− Standard deduction (${taxRegime === "new" ? "new" : "old"} regime)`, -stdDeduction],
                  ["− Other declared deductions", -otherDeductions],
                  ["= Taxable income", details.taxableAnnual, true],
                  ["Income tax + 4% cess (after rebate/relief)", incomeTaxAnnual],
                  ["+ Professional Tax", ptAnnual],
                  ["+ Labour Welfare Fund", lwfAnnual],
                  ["= Total annual tax", totalAnnualTax, true],
                ];
                return (
                  <div className="rounded-lg border overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm">
                      Tax computation ({taxRegime === "new" ? "New" : "Old"} Regime)
                    </div>
                    <div className="divide-y text-sm">
                      {rows.map(([label, val, strong]) => (
                        <div
                          key={label}
                          className={`flex justify-between px-4 py-2 ${strong ? "bg-gray-50 font-semibold text-gray-800" : ""}`}
                        >
                          <span className={strong ? "" : "text-gray-600"}>{label}</span>
                          <span className={val < 0 ? "text-red-600 font-medium" : "text-gray-900 font-medium"}>
                            {val < 0 ? "-" : ""}₹{fmt(Math.abs(val))}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between px-4 py-3 bg-amber-50">
                        <span className="font-semibold text-amber-800">Monthly tax</span>
                        <span className="font-bold text-amber-700">
                          ₹{fmt(details.tds + details.pt + details.lwfEE)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {showTaxBreakdown && (
                <p className="text-xs text-gray-500">
                  If taxable income is ₹12,00,000 or below, income tax is zero (₹60,000 rebate); just
                  above ₹12,00,000 marginal relief caps the tax at the amount over ₹12,00,000.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── PF contribution chooser (modal) ─────────────────────────── */}
      {pfModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
            onClick={() => setPfModalOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-4 py-3 border-b shrink-0">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Set PF Contribution</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Choose how your Provident Fund is calculated.
                  </p>
                </div>
                <button
                  onClick={() => setPfModalOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-4 py-4 space-y-3 overflow-y-auto flex-1 min-h-0">
                {/* Option 1 — Capped (default) */}
                <button
                  type="button"
                  onClick={() => setPfBasisDraft("statutory")}
                  className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                    pfBasisDraft === "statutory"
                      ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">Capped (Statutory)</span>
                    {pfBasisDraft === "statutory" && (
                      <span className="text-emerald-600 text-xs font-semibold">Selected</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    PF is deducted on Basic capped at ₹15,000, at the standard 12%. No % needed.
                  </p>
                </button>

                {/* Option 2 — Actual basic */}
                <button
                  type="button"
                  onClick={() => setPfBasisDraft("actual")}
                  className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                    pfBasisDraft === "actual"
                      ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">Actual Basic</span>
                    {pfBasisDraft === "actual" && (
                      <span className="text-emerald-600 text-xs font-semibold">Selected</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    PF is deducted on your full Basic salary at the % you choose below.
                  </p>
                </button>

                {/* Percent input — only for "Actual basic" */}
                {pfBasisDraft === "actual" && (
                  <div>
                    <Label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      PF % on Basic — min 12%
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      min={12}
                      autoFocus
                      value={pfRateDraft}
                      onChange={(e) => setPfRateDraft(Number(e.target.value))}
                      className="mt-1 w-32"
                    />
                  </div>
                )}

                {/* EPF account identifiers — optional. */}
                <div className="rounded-lg border bg-emerald-50/40 p-3">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    EPF account details <span className="text-gray-400 normal-case">(optional)</span>
                  </p>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[11px] font-semibold text-gray-500">UAN</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={uan}
                        onChange={(e) => setUan(e.target.value)}
                        placeholder="12-digit Universal Account Number"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold text-gray-500">PF Member ID</Label>
                      <Input
                        type="text"
                        value={pfMemberId}
                        onChange={(e) => setPfMemberId(e.target.value)}
                        placeholder="e.g. KN/BNG/0012345/000/0001234"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-gray-400">
                  You can change this until you save your declaration. After saving, PF can no longer be changed.
                </p>
              </div>
              <div className="px-4 py-3 border-t flex justify-end gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPfModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={applyPfChoice}
                  disabled={pfChooserLocked || (pfBasisDraft === "actual" && (pfRateDraft || 0) < 12)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── HRA rent-exemption calculator (modal) ───────────────────── */}
      {oldRegimeNotice &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
            onClick={() => setOldRegimeNotice(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
                <h3 className="text-base font-bold text-gray-900">Old Regime — coming soon</h3>
                <button
                  onClick={() => setOldRegimeNotice(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-4 py-4">
                <p className="text-sm text-gray-600">
                  This section is still in progress. Old Regime tax computation isn't available
                  yet — please continue with the New Regime for now.
                </p>
              </div>
              <div className="px-4 py-3 border-t flex justify-end">
                <Button onClick={() => setOldRegimeNotice(false)} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Got it
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {hraCalcOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
            onClick={() => setHraCalcOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Do you live in a rented home?</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Pay rent? Claim an HRA benefit in the Old regime to save tax.
                  </p>
                </div>
                <button
                  onClick={() => setHraCalcOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-4 py-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaysRent(true)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                      paysRent
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    Yes, I pay rent
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaysRent(false)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                      !paysRent
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    No / I own my home
                  </button>
                </div>

                {paysRent && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-gray-700">Rent you pay (/year)</Label>
                        <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden bg-white mt-1">
                          <span className="px-2.5 flex items-center bg-gray-50 text-gray-500 border-r border-gray-200 text-sm">₹</span>
                          <input
                            type="number"
                            value={annualRent || ""}
                            onChange={(e) => setAnnualRent(Number(e.target.value))}
                            placeholder="0"
                            className="flex-1 px-2.5 py-1.5 outline-none text-sm"
                          />
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">Monthly rent × 12.</p>
                      </div>

                      <div>
                        <Label className="text-xs font-semibold text-gray-700">HRA received (/year)</Label>
                        <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden bg-white mt-1">
                          <span className="px-2.5 flex items-center bg-gray-50 text-gray-500 border-r border-gray-200 text-sm">₹</span>
                          <input
                            type="number"
                            value={annualHraReceived || ""}
                            onChange={(e) => setAnnualHraReceived(Number(e.target.value))}
                            placeholder="0"
                            className="flex-1 px-2.5 py-1.5 outline-none text-sm"
                          />
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">Payslip 'HRA' × 12.</p>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-gray-700">Which city do you live in?</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setIsMetro(true)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                            isMetro
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-300 text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          Metro city
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsMetro(false)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                            !isMetro
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-300 text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          Non-metro
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Metro = Delhi, Mumbai, Kolkata, Chennai (50% of Basic); others 40%.
                      </p>
                    </div>

                    <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                      <div className="text-sm text-gray-800">
                        HRA tax-free amount:{" "}
                        <span className="font-bold text-green-700">₹{fmt(hraExemptionPreview)}</span> / year
                      </div>
                      <div className="text-[11px] text-gray-500">
                        Lowest of three legal limits, picked automatically.
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2 px-4 py-3 border-t">
                <button
                  onClick={() => setHraCalcOpen(false)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={applyHraExemption}
                  className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  {paysRent ? "Apply exemption" : "Clear exemption"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── LTA travel-exemption claim (modal) ───────────────────────── */}
      {ltaCalcOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
            onClick={() => setLtaCalcOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Claim LTA exemption</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Travelled this year? Claim an LTA benefit in the Old regime to save tax.
                  </p>
                </div>
                <button
                  onClick={() => setLtaCalcOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-4 py-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Actual travel expense (/year)</Label>
                    <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden bg-white mt-1">
                      <span className="px-2.5 flex items-center bg-gray-50 text-gray-500 border-r border-gray-200 text-sm">₹</span>
                      <input
                        type="number"
                        value={travelExpense || ""}
                        onChange={(e) => setTravelExpense(Number(e.target.value))}
                        placeholder="0"
                        className="flex-1 px-2.5 py-1.5 outline-none text-sm"
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">Travel fare only (air/train/bus).</p>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-gray-700">LTA received (/year)</Label>
                    <div className="flex items-stretch rounded-lg border border-gray-200 overflow-hidden bg-gray-100 mt-1">
                      <span className="px-2.5 flex items-center bg-gray-100 text-gray-500 border-r border-gray-200 text-sm">₹</span>
                      <input
                        type="number"
                        value={ltaReceivedAnnual || ""}
                        readOnly
                        disabled
                        placeholder="0"
                        className="flex-1 px-2.5 py-1.5 outline-none text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">Payslip 'LTA' × 12.</p>
                  </div>
                </div>

                <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                  <div className="text-sm text-gray-800">
                    LTA tax-free amount:{" "}
                    <span className="font-bold text-green-700">₹{fmt(ltaExemptionPreview)}</span> / year
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Lowest of the actual travel expense and the LTA received.
                  </div>
                </div>

                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5">
                  <p className="text-xs font-semibold text-gray-800 mb-1">
                    You can claim an exemption on LTA in Old Tax Regime under the following conditions:
                  </p>
                  <ul className="list-disc pl-4 space-y-1 text-[11px] text-gray-600">
                    <li>Travel must be within India.</li>
                    <li>Only the travel fare is exempt (air/train/bus fare as per prescribed rules).</li>
                    <li>Expenses such as hotel, food, sightseeing, taxi, etc., are not exempt.</li>
                    <li>Exemption is available for 2 journeys in a block of 4 calendar years.</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end gap-2 px-4 py-3 border-t">
                <button
                  onClick={() => setLtaCalcOpen(false)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={applyLtaExemption}
                  className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  {ltaExemptionPreview > 0 ? "Apply exemption" : "Clear exemption"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
